import { ENGAGEMENT_PERIOD_QUERY_KEY, ENGAGEMENT_SCOPE_QUERY_KEY } from "@domains/engagement/shared/queryKeys.ts";
import { DEFAULT_TIME_FRAME, TIME_FRAMES, type TimeFrame } from "@domains/engagement/shared/timeFrame.ts";
import { parseDate, today } from "@internationalized/date";
import { DEFAULT_REPORTING_SCOPE, REPORTING_SCOPES, type ReportingScope } from "@packages/core/organization/scope.ts";
import { createParser, parseAsStringEnum, useQueryState } from "nuqs";

const QUERY_STATE_OPTIONS = { history: "replace" as const, clearOnDefault: true, shallow: false };

const scopeParser = parseAsStringEnum<ReportingScope>([...REPORTING_SCOPES])
    .withDefault(DEFAULT_REPORTING_SCOPE)
    .withOptions(QUERY_STATE_OPTIONS);
const timeFrameParser = parseAsStringEnum<TimeFrame>([...TIME_FRAMES])
    .withDefault(DEFAULT_TIME_FRAME)
    .withOptions(QUERY_STATE_OPTIONS);

export function useEngagementScope() {
    return useQueryState(ENGAGEMENT_SCOPE_QUERY_KEY, scopeParser);
}

export function useEngagementTimeFrame() {
    return useQueryState(ENGAGEMENT_PERIOD_QUERY_KEY, timeFrameParser);
}

export function useEngagementFilters() {
    const [scope] = useQueryState(ENGAGEMENT_SCOPE_QUERY_KEY, scopeParser);
    const [timeFrame] = useQueryState(ENGAGEMENT_PERIOD_QUERY_KEY, timeFrameParser);

    return { scope, timeFrame };
}

const HEATMAP_CELL_MODE_QUERY_KEY = "engagement-heatmap-mode";

const CELL_MODES = ["variation", "score"] as const;
export type CellMode = (typeof CELL_MODES)[number];

const cellModeParser = parseAsStringEnum<CellMode>([...CELL_MODES])
    .withDefault("variation")
    .withOptions({ history: "replace" as const, clearOnDefault: true });

export function useHeatmapCellMode() {
    return useQueryState(HEATMAP_CELL_MODE_QUERY_KEY, cellModeParser);
}

const HEATMAP_SCORE_DATE_QUERY_KEY = "engagement-heatmap-date";

const parseAsScoreDate = createParser({
    parse: value => {
        try {
            parseDate(value);
            return value;
        } catch {
            return null;
        }
    },
    serialize: (value: string) => value
});

export function useHeatmapScoreDate() {
    return useQueryState(HEATMAP_SCORE_DATE_QUERY_KEY, parseAsScoreDate.withOptions({ history: "replace" }));
}

export function resolveHeatmapScoreDate(param: string | null): string {
    return param ?? today("UTC").toString();
}

export function toEngagementScoreDateInstant(isoDate: string): string {
    return `${isoDate}T00:00:00.000Z`;
}
